import { cn } from '@crm/ui';
import type * as React from 'react';

export interface BarChartSeries {
  key: string;
  label: string;
  /** Classe de fundo da barra, ex.: `bg-blue-500`. */
  color: string;
  /** Cor do traco no modo linha, ex.: `text-blue-500`. Cai no azul da marca. */
  stroke?: string;
}

/** Como o periodo e desenhado: colunas ou uma linha ligando os pontos. */
export type ChartVariant = 'bars' | 'line';

interface BarChartProps {
  data: {
    label: string;
    /** Rotulo desenhado, quando o texto puro nao basta (ex.: ano menor). */
    labelNode?: React.ReactNode;
    values: Record<string, number>;
    tooltip?: string;
  }[];
  series: BarChartSeries[];
  formatValue?: (value: number) => string;
  /** Altura da area das barras, em pixels. */
  height?: number;
  variant?: ChartVariant;
  /** Texto quando todas as series estao zeradas. */
  emptyLabel?: string;
  /**
   * Desenha o grafico zerado em vez do aviso. Serve para a serie que se
   * navega: numa janela sem movimento, a linha no chao diz "nada aqui" sem
   * fazer o grafico sumir e voltar a cada passo.
   */
  drawWhenEmpty?: boolean;
  /**
   * Inclina os rotulos do eixo. Data completa nao cabe deitada numa coluna
   * estreita - truncada, "09/07" perdia justamente o mes.
   */
  tiltLabels?: boolean;
  /** Controle que divide a linha da legenda (ex.: a troca de semestre). */
  aside?: React.ReactNode;
  className?: string;
}

/**
 * Grafico em CSS puro (mais um `polyline` no modo linha) - sem lib, que para
 * uma serie por coluna seria peso sem retorno. A escala e a do maior valor.
 */
export function BarChart({
  data,
  series,
  formatValue = (value) => String(value),
  height = 200,
  variant = 'bars',
  emptyLabel = 'Nada previsto no periodo.',
  drawWhenEmpty,
  tiltLabels,
  aside,
  className,
}: BarChartProps) {
  const max = Math.max(
    1,
    ...data.flatMap((point) => series.map((entry) => point.values[entry.key] ?? 0)),
  );
  // Sem nenhum valor as barras somem e sobra um retangulo vazio, que se le
  // como grafico quebrado - melhor dizer que nao ha o que mostrar.
  const vazio =
    !drawWhenEmpty && data.every((point) => series.every((entry) => !point.values[entry.key]));
  const alturaVar = { '--chart-height': `${height}px` } as React.CSSProperties;
  /**
   * Um rotulo a cada tantos pontos. Os 31 dias de um mes escritos embaixo de
   * colunas de 10px viram uma tarja preta; de cinco em cinco a escala continua
   * legivel e as colunas seguem todas ali.
   */
  /**
   * Os pontos que merecem numero escrito no modo linha: o pico e o vale do
   * periodo, mais o ultimo ponto. Escrever todos viraria uma parede de
   * numeros em cima da curva; escrever nenhum obriga a passar o mouse ponto
   * a ponto para saber onde se esta.
   */
  const notaveis = (() => {
    if (variant !== 'line' || series.length !== 1) return new Set<number>();

    const chave = series[0]?.key;
    if (!chave) return new Set<number>();

    const valores = data.map((point) => point.values[chave] ?? 0);
    if (valores.every((valor) => !valor)) return new Set<number>();

    const maior = valores.indexOf(Math.max(...valores));
    // O vale so interessa entre pontos com movimento - zero no fim de um
    // periodo que ainda nao aconteceu nao e vale, e ausencia.
    const comMovimento = valores.filter((valor) => valor > 0);
    const menor = comMovimento.length > 1 ? valores.indexOf(Math.min(...comMovimento)) : -1;

    return new Set([maior, menor, valores.length - 1].filter((indice) => indice >= 0));
  })();

  // Inclinado, o rotulo nao disputa largura com o vizinho: cabem todos.
  const passoDoRotulo = tiltLabels || data.length <= 16 ? 1 : Math.ceil(data.length / 8);

  /**
   * Traco da serie no modo linha. A curva passa por todos os pontos, com as
   * alcas a um terco do vizinho: em `polyline` cada mes virava um bico, e a
   * leitura era de serra em vez de tendencia.
   */
  const tracar = (chave: string) => {
    const pontos = data.map((point, index) => ({
      x: ((index + 0.5) / data.length) * 100,
      y: 100 - ((point.values[chave] ?? 0) / max) * 100,
    }));

    return pontos
      .map((ponto, index) => {
        const anterior = pontos[index - 1];
        if (index === 0 || !anterior) return `M ${ponto.x},${ponto.y}`;

        const meio = (ponto.x - anterior.x) / 3;

        return `C ${anterior.x + meio},${anterior.y} ${ponto.x - meio},${ponto.y} ${ponto.x},${ponto.y}`;
      })
      .join(' ');
  };

  if (vazio) {
    return (
      <div className={cn('space-y-3', className)}>
        {aside ? <div className="flex justify-end">{aside}</div> : null}
        <p
          className="flex h-32 items-center justify-center text-center text-sm text-muted-foreground sm:h-[var(--chart-height)]"
          style={alturaVar}
        >
          {emptyLabel}
        </p>
      </div>
    );
  }

  return (
    <div className={cn('space-y-3', className)}>
      {/* Legenda de um lado, controle do periodo do outro: em linhas
          separadas eles empurravam as barras para baixo. Com uma serie so a
          legenda seria a repeticao do titulo do cartao - fica de fora. */}
      {series.length > 1 || aside ? (
        <div className="flex items-center justify-between gap-4">
          <div className="flex flex-wrap gap-4">
            {series.length > 1
              ? series.map((entry) => (
                  <span
                    key={entry.key}
                    className="flex items-center gap-1.5 text-xs text-muted-foreground"
                  >
                    <span className={cn('h-2.5 w-2.5 rounded-sm', entry.color)} />
                    {entry.label}
                  </span>
                ))
              : null}
          </div>

          {aside}
        </div>
      ) : null}

      {/* As colunas dividem a largura disponivel em vez de forcar rolagem:
          uma barra de rolagem dentro da pagina que ja rola e um gesto a mais
          para ver o mes inteiro. */}
      {/* Folga no topo: o numero do ponto mais alto fica acima da barra, e
          sem esse respiro ele encostava no cabecalho do cartao. */}
      <div className="space-y-1 pt-5">
        {/* Area do desenho, separada dos rotulos: assim a linha do modo
              `line` ocupa exatamente a altura das barras. */}
        <div className="relative flex h-32 gap-1.5 sm:h-[var(--chart-height)]" style={alturaVar}>
          {/* Grade de referencia: sem ela o olho nao compara alturas entre
              pontos distantes. Bem clara, para nao virar assunto. */}
          <div
            aria-hidden
            className="pointer-events-none absolute inset-0 z-0 flex flex-col justify-between"
          >
            {[0, 1, 2, 3, 4].map((linha) => (
              <span key={linha} className="block border-t border-border/60" />
            ))}
          </div>

          {variant === 'line' ? (
            <svg
              className="pointer-events-none absolute inset-0 h-full w-full overflow-visible"
              viewBox="0 0 100 100"
              preserveAspectRatio="none"
              aria-hidden
            >
              <title>{series.map((entry) => entry.label).join(', ')}</title>
              {series.map((entry) => (
                <path
                  key={entry.key}
                  className={entry.stroke ?? 'text-primary'}
                  d={tracar(entry.key)}
                  fill="none"
                  stroke="currentColor"
                  strokeWidth={2}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  vectorEffect="non-scaling-stroke"
                />
              ))}
            </svg>
          ) : null}

          {data.map((point, indice) => (
            <div
              key={point.label}
              className="group relative z-10 flex min-w-0 flex-1 items-end justify-center gap-0.5 sm:gap-1"
              title={
                point.tooltip ??
                series
                  .map(
                    (entry) =>
                      `${point.label} - ${entry.label}: ${formatValue(point.values[entry.key] ?? 0)}`,
                  )
                  .join('\n')
              }
            >
              {/* O numero fica a vista: no topo de cada coluna, e nos pontos
                  notaveis da curva. Escondido atras do ponteiro, obrigava a
                  varrer o grafico para ler qualquer coisa. */}
              {variant === 'bars' ? (
                // Encostado no topo da propria barra, nao no teto do
                // grafico: a coluna baixa tinha o numero flutuando longe
                // dela, e a leitura pedia mira.
                <span
                  className="pointer-events-none absolute left-1/2 -translate-x-1/2 whitespace-nowrap px-1 text-[10px] font-medium tabular-nums sm:text-xs"
                  style={{
                    bottom: `calc(${
                      (Math.max(...series.map((entry) => point.values[entry.key] ?? 0)) / max) * 100
                    }% + 0.15rem)`,
                  }}
                >
                  {series.map((entry) => formatValue(point.values[entry.key] ?? 0)).join(' / ')}
                </span>
              ) : null}

              {variant === 'line' && notaveis.has(indice) ? (
                <>
                  {/* O ponto marca de que altura o numero fala - sem ele o
                      rotulo flutua solto sobre a curva. */}
                  <span
                    aria-hidden
                    className={cn(
                      'pointer-events-none absolute left-1/2 h-2 w-2 -translate-x-1/2 translate-y-1/2 rounded-full bg-current',
                      series[0]?.stroke ?? 'text-primary',
                    )}
                    style={{
                      bottom: `${((point.values[series[0]?.key ?? ''] ?? 0) / max) * 100}%`,
                    }}
                  />
                  <span
                    className="pointer-events-none absolute left-1/2 -translate-x-1/2 whitespace-nowrap px-1 text-[10px] font-medium tabular-nums sm:text-xs"
                    style={{
                      bottom: `calc(${((point.values[series[0]?.key ?? ''] ?? 0) / max) * 100}% + 0.55rem)`,
                    }}
                  >
                    {formatValue(point.values[series[0]?.key ?? ''] ?? 0)}
                  </span>
                </>
              ) : null}

              {variant === 'bars'
                ? series.map((entry) => {
                    const value = point.values[entry.key] ?? 0;

                    return (
                      <div
                        key={entry.key}
                        // Barra zerada vira um tracinho: some da tela, mas o
                        // ponto continua ali para nao quebrar a sequencia.
                        className={cn(
                          // Um fio mais claro no topo da coluna: separa a
                          // barra do fundo sem virar contorno.
                          'w-full max-w-5 rounded-t-sm border-t-2 border-white/40 sm:max-w-6 dark:border-white/25',
                          entry.color,
                          !value && 'h-px border-t-0',
                        )}
                        style={value ? { height: `${(value / max) * 100}%` } : undefined}
                      />
                    );
                  })
                : null}
            </div>
          ))}
        </div>

        <div className={cn('flex gap-1.5', tiltLabels && 'h-14')}>
          {data.map((point, index) =>
            tiltLabels ? (
              <span key={point.label} className="relative min-w-0 flex-1">
                <span className="absolute right-1/2 top-1 origin-top-right -rotate-[70deg] whitespace-nowrap text-sm text-muted-foreground">
                  {point.labelNode ?? point.label}
                </span>
              </span>
            ) : (
              <span
                key={point.label}
                className="min-w-0 flex-1 truncate text-center text-[10px] text-muted-foreground sm:text-xs"
              >
                {index % passoDoRotulo === 0 ? (point.labelNode ?? point.label) : ''}
              </span>
            ),
          )}
        </div>
      </div>
    </div>
  );
}
